import React, { useEffect, useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Link from '@atlaskit/link/link';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import Stack from '@atlaskit/primitives/stack';
import { xcss } from '@atlaskit/primitives/xcss';
import { getActiveTrace } from '@atlaskit/react-ufo/experience-trace-id-context';

import {
	getGraphqlErrorAttributes,
	getReactErrorAttributes,
} from '@confluence/monitoring/entry-points/error-attributes.ts';

import { ATLASSIAN_SUPPORT_URL } from './AtlassianSupportLink.tsx';
import { CodeBlockWithCopyButton } from './CodeBlockWithCopy.tsx';

const CONFLUENCE_STATUS_PAGE_URL = 'https://confluence.status.atlassian.com/';
const ERROR_COMPONENT_KB_ARTICLE_URL =
	'https://confluence.atlassian.com/confkb/getting-we-ve-hit-a-snag-error-message-when-viewing-confluence-pages-1095238852.html';

export const i18n: {
	description: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	status403Description: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	errorHash: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	errorHashInCodeBlock: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	url: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	trace: {
		id: string;
		description: string;
		defaultMessage: string;
	};
} = defineMessages({
	description: {
		id: 'error-boundary.description',
		description:
			"Additional text shown when displaying a technical error. {atlassianSupportLink} will be substituted with a message that has id='error-boundary.support-link-text' which reads as 'our support team'. This phrase continues with a list of bullet points",
		defaultMessage:
			'The issue should resolve on its own. In the meantime, you can check the <statuslink>status page</statuslink>. There may be a known incident reported related to <kblink>this error</kblink>. If it keeps happening, ask your admin to contact <supportlink>our support team</supportlink> and give them:',
	},
	status403Description: {
		id: 'error-boundary.status.403.description',
		description:
			"Additional text shown when displaying a technical error. {atlassianSupportLink} will be substituted with a message that has id='error-boundary.support-link-text' which reads as 'our support team'. This phrase continues with a list of bullet points",
		defaultMessage:
			'To resolve this, try reloading or reach out to your admin about getting access. You can also check the <statuslink>status page</statuslink> about a potential known incident related to this <kblink>error</kblink>. If it keeps happening, ask your admin to contact <supportlink>support</supportlink> and give them:',
	},
	errorHash: {
		id: 'error-boundary.code',
		description:
			'The code field in error boundary component {code} will be substituted with a string of digits and letters.',
		defaultMessage: 'The code associated with this error: {code}',
	},
	errorHashInCodeBlock: {
		id: 'error-boundary.code.in-code-block',
		description:
			'The code field in error boundary component {code} will be substituted with a string of digits and letters shown in a code block.',
		defaultMessage: 'Hash: {code}',
	},
	url: {
		id: 'error-boundary.url',
		description: 'The url field in error boundary component',
		defaultMessage: 'The URL of this page',
	},
	trace: {
		id: 'error-boundary.trace',
		description:
			'The traceid field in error boundary component. {traceId} will be substituted with a string of digits and letters that represents a trace of the user journey through the application.',
		defaultMessage: 'Trace: {traceId}',
	},
});

const codeBlockContainerStyles = xcss({
	maxWidth: '330px',
});

type ErrorDetailProps = {
	error?: Error;
	isFullSizeErrorComponent?: boolean;
};

export function ErrorDetail({
	error,
	isFullSizeErrorComponent,
}: ErrorDetailProps): React.JSX.Element | null {
	const graphqlErrorAttributes = !!error ? getGraphqlErrorAttributes(error) : undefined;
	const reactErrorAttributes = !!error ? getReactErrorAttributes(error) : undefined;
	const experienceTraceContext = getActiveTrace();

	const errorHashCode = reactErrorAttributes ? reactErrorAttributes.errorHashCode : undefined;
	const traceId = experienceTraceContext?.traceId ?? graphqlErrorAttributes?.traceId;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();

	const codeBlockLines = useMemo(() => {
		const lines: string[] = [];

		if (errorHashCode) {
			lines.push(
				intl.formatMessage(i18n.errorHashInCodeBlock, {
					code: errorHashCode,
				}),
			);
		}

		if (traceId) {
			lines.push(intl.formatMessage(i18n.trace, { traceId }));
		}

		return lines;
	}, [intl, errorHashCode, traceId]);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'errorDetail',
				attributes: {
					errorHashCode,
					traceId,
				},
			},
		})?.fire();
	}, [createAnalyticsEvent, errorHashCode, traceId]);

	const i18nDescription =
		graphqlErrorAttributes?.statusCode === 403 ? i18n.status403Description : i18n.description;

	return codeBlockLines.length > 0 ? (
		<Stack space="space.100">
			<FormattedMessage
				{...i18nDescription}
				tagName="p"
				values={{
					supportlink: (chunks) => (
						<InlineLink url={ATLASSIAN_SUPPORT_URL} chunks={chunks} analyticsName="supportLink" />
					),
					statuslink: (chunks) => (
						<InlineLink
							url={CONFLUENCE_STATUS_PAGE_URL}
							chunks={chunks}
							analyticsName="statusPageLink"
						/>
					),
					kblink: (chunks) => (
						<InlineLink
							url={ERROR_COMPONENT_KB_ARTICLE_URL}
							chunks={chunks}
							analyticsName="knowledgebaseLink"
						/>
					),
				}}
			/>

			<Box xcss={isFullSizeErrorComponent && codeBlockContainerStyles}>
				<CodeBlockWithCopyButton lines={codeBlockLines} analyticsSource="errorDetail" />
			</Box>
		</Stack>
	) : null;
}

export const InlineLink = ({
	url,
	chunks,
	analyticsName,
}: {
	url: string;
	chunks: React.ReactNode[];
	analyticsName: string;
}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: analyticsName,
				source: 'errorDetail',
			},
		}).fire();
	};

	return (
		<Link href={url} target="_blank" onClick={fireClickAnalytics}>
			{chunks}
		</Link>
	);
};
